import React, { PureComponent } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  InjectedIntl,
} from 'react-intl';

import Button from '@atlaskit/button';
import DropdownMenuStateless, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu-12';
import { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import CommitsIcon from '@atlaskit/icon/glyph/bitbucket/commits';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Spinner from '@atlaskit/spinner';

import { Commit } from 'src/components/types';
import { commonMessages } from 'src/i18n';
import { shortHash } from 'src/utils/short-hash';

import CommitSelectorItem from './commit-selector-item';

const messages = defineMessages({
  loadMore: {
    id: 'frontbucket.repository.source.file.commitSelector.loadMore',
    description:
      'Dropdown item that loads earlier commits in the dropdown menu when selected',
    defaultMessage: 'Show more',
  },
  menuTitle: {
    id: 'frontbucket.repository.source.file.commitSelector.menuTitle',
    description:
      'Title of the dropdown menu for selecting a commit to view a file at',
    defaultMessage: 'Commit history',
  },
});

const CenteredDropdownItem = styled(DropdownItem)<{ isDisabled: boolean }>`
  text-align: center;
`;

export const LoadMoreItem = (props: any) => (
  <CenteredDropdownItem {...props}>
    <FormattedMessage {...messages.loadMore} />
  </CenteredDropdownItem>
);

export const LoadingItem = () => (
  <CenteredDropdownItem isDisabled>
    <Spinner size="small" />
  </CenteredDropdownItem>
);

type Props = {
  commits: Commit[];
  hasMore: boolean;
  intl: InjectedIntl;
  isLoading: boolean;
  onChange: (commit: Commit) => void;
  onLoadMore: () => void;
  selectedHash: string | null | undefined;
};

type State = {
  closingFromLoadMore: boolean;
  isOpen: boolean;
};

class CommitSelector extends PureComponent<Props, State> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    onChange: () => {},
    onLoadMore: () => {},
  };

  state: State = {
    closingFromLoadMore: false,
    isOpen: false,
  };

  // Block the Dropdown from closing when "load more" is selected
  handleOpenChange = (attrs: OnOpenChangeArgs) =>
    this.setState((prevState: State) =>
      prevState.closingFromLoadMore
        ? ({ closingFromLoadMore: false } as State)
        : ({ isOpen: attrs.isOpen } as State)
    );

  render() {
    const { isLoading, selectedHash } = this.props;

    return (
      <DropdownMenuStateless
        isOpen={this.state.isOpen}
        onOpenChange={this.handleOpenChange}
        trigger={({ triggerRef, ...props }) => (
          <Button
            {...props}
            iconBefore={<CommitsIcon size="medium" label="" />}
            iconAfter={
              <ChevronDownIcon
                label={this.props.intl.formatMessage(commonMessages.expandIcon)}
              />
            }
            ref={triggerRef}
          >
            {selectedHash ? shortHash(selectedHash) : ''}
          </Button>
        )}
      >
        <DropdownItemGroup
          title={this.props.intl.formatMessage(messages.menuTitle)}
        >
          {this.props.commits.map(commit => (
            <CommitSelectorItem
              commit={commit}
              isSelected={commit.hash === selectedHash}
              key={commit.hash}
              onSelect={this.props.onChange}
            />
          ))}
          {this.props.hasMore && !isLoading && (
            <LoadMoreItem
              onClick={() => {
                this.props.onLoadMore();
                this.setState({ closingFromLoadMore: true });
              }}
            />
          )}
          {isLoading && <LoadingItem />}
        </DropdownItemGroup>
      </DropdownMenuStateless>
    );
  }
}

export default injectIntl(CommitSelector);
